import { round } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Favorite_PageIdEnum, Trip, TripsAggregations } from 'src/api/gen'
import { ITripListFilters } from 'src/api/types/trips'
import useSafeState from 'src/commons/hooks/useSafeState'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { userHasPermission } from 'src/commons/permissions'
import { useAgencySettingsDistanceUnit, useAgencySettingsSpeedUnit } from 'src/layout/AgencySettings'
import { useProviders, useCityBoundaries } from 'src/layout/hooks'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'

import { getInitialFilters, INITIAL_FACETS } from '../common'
import { SECONDS_IN_MINUTE } from '../constants'
import FilterPanel from '../FilterPanel'
import { fetchExportTripList, fetchFacetedTripList } from '../requests'
import { tripCountsInStreetSegments } from '../tests/mockedData'

import Table from './Table'

function TripsTable() {
  const { t } = useTranslation()
  const { activeProviders } = useProviders()
  const cityBoundaries = useCityBoundaries()
  const { value: speedUnit } = useAgencySettingsSpeedUnit()
  const { value: distanceUnit } = useAgencySettingsDistanceUnit()

  const defaultFilters = useMemo(
    () => getInitialFilters(cityBoundaries, speedUnit, distanceUnit),
    [cityBoundaries, speedUnit, distanceUnit]
  )
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.trips_list, defaultFilters, undefined, {
    dateStartRange: defaultFilters.dateStartRange,
    dateEndRange: defaultFilters.dateEndRange,
  })
  const displayedFiltersRef = useRef<ITripListFilters>()

  const [tripsFacets, setTripsFacets] = useSafeState<TripsAggregations>(INITIAL_FACETS)

  const [trips, setTrips] = useSafeState<Trip[]>([])
  const [tripsCount, setTripsCount] = useSafeState<number>(0)
  const [isLoading, setIsLoading] = useSafeState<boolean>(false)

  const fetchDataForTripList = async function () {
    try {
      setIsLoading(true)

      const facetedListResponse = await fetchFacetedTripList(filters, 0)

      if (!facetedListResponse.documents.length) {
        setTrips([])
        setTripsCount(0)
        setTripsFacets(INITIAL_FACETS)
        return
      }

      let { duration_min, duration_max } = facetedListResponse.aggregations

      duration_min = round((duration_min || 0) / SECONDS_IN_MINUTE)
      duration_max = round((duration_max || 0) / SECONDS_IN_MINUTE)

      setTrips(facetedListResponse.documents)
      setTripsCount(facetedListResponse.number_of_matching_documents)
      setTripsFacets({ ...facetedListResponse.aggregations, duration_min, duration_max })
    } catch (error) {
      console.error('ERROR while fetching trips list and filters data', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchDataForTripList()
  }, [filters])

  const onChangeMapLayer = useCallback(
    (key: string) => {
      setFilters({ ...filters, origins: [], destinations: [], layer: key })
    },
    [filters]
  )

  function onIdVinFilterChange(input: string) {
    setFilters((pendingFilters) => ({ ...pendingFilters, id: input }))
  }

  let onClickExport: (() => Promise<void>) | undefined = undefined
  if (userHasPermission(['analyze', 'trip'])) {
    onClickExport = () => fetchExportTripList(filters)
  }

  const onApplyFilterClick = function () {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters((pendingFilters) => ({ ...pendingFilters, ...displayedFiltersRef.current }))
  }

  return (
    <FilteringCard
      SideComponent={
        <FilterPanel
          ref={displayedFiltersRef}
          initialFilters={filters}
          defaultFilters={defaultFilters}
          providers={activeProviders}
          tripsCount={tripsCount}
          tripsFacets={tripsFacets}
          onChangeMapLayer={onChangeMapLayer}
          isHeatmapTripsSelected={false}
          isSensorsTripsEnabled={false}
          tripCountsInStreetSegments={tripCountsInStreetSegments}
        />
      }
      onApplyFilter={onApplyFilterClick}
    >
      <Table
        data={trips}
        isLoading={isLoading}
        totalCount={tripsCount}
        onClickExport={onClickExport}
        searchBar={{
          value: filters.id,
          onChange: onIdVinFilterChange,
          placeholder: t('trips.idOrVin'),
        }}
      />
    </FilteringCard>
  )
}
export default TripsTable
